/**
 * Task order (project) active-window helpers.
 *
 * A task order is active when today falls inside [start_date, end_date].
 * A NULL start_date means open-ended in the past; a NULL end_date means it
 * never expires. Undated task orders are therefore always active.
 */

const toDayNumber = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  // Compare on calendar days so a task order is usable through the whole of its end date.
  return Date.UTC(parsed.getUTCFullYear(), parsed.getUTCMonth(), parsed.getUTCDate());
};

const todayNumber = () => {
  const now = new Date();
  return Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
};

export const isTaskOrderActive = (project, today = todayNumber()) => {
  if (!project) return false;

  const start = toDayNumber(project.start_date);
  const end = toDayNumber(project.end_date);

  if (start !== null && today < start) return false;
  if (end !== null && today > end) return false;
  return true;
};

/** Newest start date first; undated task orders sort last, then by name. */
export const sortTaskOrders = (projects = []) =>
  [...projects].sort((a, b) => {
    const aStart = toDayNumber(a?.start_date);
    const bStart = toDayNumber(b?.start_date);

    if (aStart === null && bStart === null) {
      return String(a?.name || '').localeCompare(String(b?.name || ''));
    }
    if (aStart === null) return 1;
    if (bStart === null) return -1;
    if (aStart !== bStart) return bStart - aStart;
    return String(a?.name || '').localeCompare(String(b?.name || ''));
  });

/**
 * Task orders offered when assigning one to an item.
 *
 * Expired/not-yet-started task orders are excluded so they cannot be picked for
 * new items, but `currentProjectId` is always kept: an existing item pointing at
 * an expired task order must still render its own value instead of going blank.
 */
export const selectableTaskOrders = (projects = [], currentProjectId = null) =>
  sortTaskOrders(
    projects.filter(
      (project) =>
        isTaskOrderActive(project) ||
        (currentProjectId != null && String(project?.id) === String(currentProjectId))
    )
  );
